import { motion } from 'framer-motion'
import {
  Newspaper, Download, Mail, ExternalLink, Calendar, ArrowRight
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import SEO, { schemas } from '../components/SEO'

const mediaKitFiles = [
  { size: '2.5 MB', format: 'PNG, SVG, AI' },
  { size: '1.2 MB', format: 'PDF' },
  { size: '15 MB', format: 'JPG' },
  { size: '8 MB', format: 'PNG' },
]

const mediaSources = ['Webrazzi', 'TechCrunch', 'ShiftDelete', 'Kamupersoneli.net']

const statValues = ['50+', '10M+', '4.8']

export default function Press() {
  const { t } = useTranslation(['press', 'common'])

  const pressReleases = t('releases.items', { returnObjects: true }) as {
    date: string; title: string; excerpt: string; category: string
  }[]

  const mediaKitNames = t('mediaKit.items', { returnObjects: true }) as string[]
  const mediaKit = mediaKitFiles.map((file, i) => ({ name: mediaKitNames[i], ...file }))

  const coverageTexts = t('coverage.items', { returnObjects: true }) as { title: string; date: string }[]
  const mediaCoverage = mediaSources.map((source, i) => ({ source, ...coverageTexts[i] }))

  const statLabels = t('stats', { returnObjects: true }) as string[]
  const stats = statValues.map((value, i) => ({ value, label: statLabels[i] }))

  const quickFacts = t('facts.items', { returnObjects: true }) as string[]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/press"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.press'), url: '/press' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Newspaper className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('hero.text')}
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-3 gap-6 mb-20"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="glass rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Press Releases */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-2xl font-display font-bold text-white mb-6">
                {t('releases.title')}
              </h2>
              <div className="space-y-4">
                {pressReleases.map((release) => (
                  <motion.article
                    key={release.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="glass rounded-xl p-6 group cursor-pointer hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                        {release.category}
                      </span>
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {release.date}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                      {release.title}
                    </h3>
                    <p className="text-gray-400 text-sm">{release.excerpt}</p>
                  </motion.article>
                ))}
              </div>
            </motion.div>

            {/* Media Coverage */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-display font-bold text-white mb-6">
                {t('coverage.title')}
              </h2>
              <div className="glass rounded-xl overflow-hidden">
                {mediaCoverage.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
                  >
                    <div>
                      <div className="text-primary text-sm font-medium mb-1">{item.source}</div>
                      <h4 className="text-white font-medium">{item.title}</h4>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500 text-sm">{item.date}</span>
                      <button className="p-2 text-gray-400 hover:text-primary transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Media Kit */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                {t('mediaKit.title')}
              </h3>
              <div className="space-y-3">
                {mediaKit.map((item) => (
                  <button
                    key={item.name}
                    className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-primary/20 transition-all group text-left"
                  >
                    <div>
                      <div className="text-white text-sm font-medium group-hover:text-primary transition-colors">
                        {item.name}
                      </div>
                      <div className="text-gray-500 text-xs">{item.format} • {item.size}</div>
                    </div>
                    <Download className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                {t('contact.title')}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {t('contact.text')}
              </p>
              <a
                href="mailto:basin@cmdesk.help"
                className="flex items-center gap-2 text-primary hover:text-primary-dark font-medium transition-colors"
              >
                basin@cmdesk.help
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Quick Facts */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4">{t('facts.title')}</h3>
              <ul className="space-y-3 text-sm">
                {quickFacts.map((fact) => (
                  <li key={fact} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                    <span className="text-gray-400">{fact}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
